import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useDrop } from "react-dnd";
import { Document, Page, pdfjs } from "react-pdf";
import DraggableField from "./draggable-field";
import SignableField from "@/components/document-viewer/signable-field";
import type { SignableFieldData } from "@/components/document-viewer/signable-field";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ZoomInIcon,
  ZoomOutIcon,
  UploadIcon,
} from "lucide-react";

// Import styles for PDF annotations and text layer
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Set the worker source to our local file
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

// Error boundary to gracefully handle react-pdf worker lifecycle errors (e.g. React strict mode)
class PdfErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(_prevProps: any, prevState: { hasError: boolean }) {
    if (this.state.hasError && !prevState.hasError) {
      // Auto-retry: strict mode worker errors are transient
      setTimeout(() => this.setState({ hasError: false }), 0);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

// Create a custom options object that adds the auth token to PDF requests
const getAuthOptions = () => {
  const token = localStorage.getItem('token');
  return {
    httpHeaders: token ? { 'Authorization': `Bearer ${token}` } : undefined,
  };
};

interface DocumentCanvasProps {
  documentUrl?: string;
  onFieldsChange?: (fields: PlacedField[]) => void;
  onDocumentUpload?: (file: File) => void;
  fields?: PlacedField[];
  /** When true, renders interactive SignableField overlays instead of DraggableField */
  signingMode?: boolean;
  /** Signing-mode fields with value/isSignerField metadata */
  signableFields?: SignableFieldData[];
  /** Called when signer clicks an unfilled field in signing mode */
  onFieldClick?: (field: SignableFieldData) => void;
}

export interface PlacedField {
  id: string;
  type: string;
  label: string;
  x: number;
  y: number;
  pageNumber: number;
  width?: number;
  height?: number;
  recipientId?: string;
  readOnly?: boolean;
  iconColor?: string;
  signatureImage?: string;
  fullName?: string;
  customFieldId?: number;
}

const DocumentCanvas: React.FC<DocumentCanvasProps> = ({
  documentUrl,
  onFieldsChange,
  onDocumentUpload,
  fields = [],
  signingMode = false,
  signableFields = [],
  onFieldClick,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0); // Default zoom set to 100%
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [documentLoaded, setDocumentLoaded] = useState<boolean>(false);
  const [showArrow, setShowArrow] = useState(true);
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentPageRef = useRef<number>(1);

  // Find first incomplete signer field on the current page for the arrow indicator
  const firstIncompleteField = useMemo(() => {
    if (!signingMode) return null;
    return signableFields.find(
      (f) =>
        Number(f.pageNumber) === Number(pageNumber) &&
        f.isSignerField !== false &&
        !f.value &&
        !f.signatureImage
    ) ?? null;
  }, [signingMode, signableFields, pageNumber]);

  // Reset arrow visibility when the target field changes
  useEffect(() => {
    if (firstIncompleteField) setShowArrow(true);
  }, [firstIncompleteField?.id]);

  // Memoize the PDF options to prevent unnecessary re-renders
  const pdfOptions = useMemo(() => getAuthOptions(), []);

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log(`Document loaded successfully with ${numPages} pages`);
    setNumPages(numPages);
    setLoading(false);
    setDocumentLoaded(true);
    // Reset to the first page when a new document is loaded
    setPageNumber(1);
    currentPageRef.current = 1;
    setError(null);
  };

  const handleDocumentLoadError = (err: Error) => {
    console.error("Error loading document:", err);
    setError(err);
    setLoading(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onDocumentUpload) {
      setLoading(true);
      onDocumentUpload(file);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Add a specific function to handle field placement that explicitly uses the current page
  const addField = useCallback((fieldType: string, fieldLabel: string, x: number, y: number, recipientId?: string, iconColor?: string, customFieldId?: number) => {
    const newField: PlacedField = {
      id: `${fieldType}-${Date.now()}`,
      type: fieldType,
      label: fieldLabel,
      x,
      y,
      pageNumber: pageNumber,
      width: 100,
      height: 40,
      recipientId,
      iconColor,
      customFieldId,
    };
    
    if (onFieldsChange) {
      onFieldsChange([...fields, newField]);
    }
  }, [pageNumber, fields, onFieldsChange]);

  // Handle moving an existing placed field
  const moveField = useCallback((fieldId: string, x: number, y: number) => {
    if (onFieldsChange) {
      onFieldsChange(fields.map(field => 
        field.id === fieldId 
          ? { ...field, x, y }
          : field
      ));
    }
  }, [fields, onFieldsChange]);

  // Create a memoized drop handler that updates when pageNumber changes
  const [, drop] = useDrop(() => ({
    accept: ["field", "placed-field"],
    drop: (item: any, monitor) => {
      if (!canvasRef.current) return;

      const canvasRect = canvasRef.current.getBoundingClientRect();
      const sourceOffset = monitor.getSourceClientOffset();

      if (!sourceOffset) return;

      // Use the source element position (top-left corner of the dragged element)
      // instead of cursor position for more accurate placement
      const x = ((sourceOffset.x - canvasRect.left) / canvasRect.width) * 100;
      const y = ((sourceOffset.y - canvasRect.top) / canvasRect.height) * 100;

      // Check if this is a placed field being moved or a new field being added
      const itemType = monitor.getItemType();
      if (itemType === "placed-field") {
        // Move existing field
        moveField(item.id, x, y);
      } else {
        // Add new field
        addField(item.type, item.label, x, y, item.recipientId, item.iconColor, item.customFieldId);
      }
    },
  }), [addField, moveField]); // Re-create the drop handler when addField or moveField changes

  // Store drop in a mutable ref so the stable callback always uses the latest
  const dropRefLatest = useRef(drop);
  dropRefLatest.current = drop;

  // Stable callback ref for the canvas drop target - never changes
  const canvasDropRef = useCallback((node: HTMLDivElement | null) => {
    canvasRef.current = node;
    dropRefLatest.current(node);
  }, []);

  const removeField = (id: string) => {
    if (onFieldsChange) {
      onFieldsChange(fields.filter((field) => field.id !== id));
    }
  };


  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    if (numPages !== null) {
      setPageNumber((prev) => Math.min(prev + 1, numPages));
    }
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 2.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const onRenderSuccess = () => {
    console.log(`Page ${pageNumber} rendered successfully`);
  };

  return (
    <div className="flex flex-col items-center w-full h-full">
      {!documentUrl ? (
        <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-border rounded-lg w-full max-w-2xl h-[600px]">
          <UploadIcon className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No document uploaded</h3>
          <p className="text-sm text-muted-foreground mb-2 text-center max-w-md">
            Upload a PDF, DOC, or DOCX document to start adding fields and
            preparing it for signatures
          </p>
          <p className="text-xs text-muted-foreground mb-6 text-center">
            Maximum file size: 50 MB. For larger files, consider splitting or compressing.
          </p>
          <Button onClick={triggerFileUpload} size="lg">
            <UploadIcon className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx"
            className="hidden"
          />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center gap-2 mb-2 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm">
              Page {pageNumber} of {numPages || "?"}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={numPages === null || pageNumber >= numPages}
            >
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </Button>

            <div className="border-l border-border h-6 mx-2" />

            <Button variant="outline" size="icon" onClick={zoomOut}>
              <ZoomOutIcon className="h-4 w-4" />
            </Button>
            <span className="text-sm">{Math.round(scale * 100)}%</span>
            <Button variant="outline" size="icon" onClick={zoomIn}>
              <ZoomInIcon className="h-4 w-4" />
            </Button>
          </div>

          <div
            ref={canvasDropRef}
            className="relative bg-white shadow-md rounded-md mx-auto overflow-visible"
            style={{ width: documentLoaded ? "fit-content" : `${scale * 100}%`, maxWidth: "98%" }}
          >
            {loading && (
              <div className="flex flex-col items-center justify-center p-12">
                <Skeleton className="h-[1100px] w-full" />
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center p-12 border border-destructive/50 rounded-md">
                <p className="text-destructive">
                  Error loading document: {error.message}
                </p>
              </div>
            )}

            {documentUrl && !loading && !error && (
              <PdfErrorBoundary fallback={<Skeleton className="h-[1100px] w-full" />}>
              <Document
                key={documentUrl}
                file={documentUrl}
                options={pdfOptions}
                onLoadSuccess={handleDocumentLoadSuccess}
                onLoadError={handleDocumentLoadError}
                loading={<Skeleton className="h-[1100px] w-full" />}
                error={
                  <div className="flex flex-col items-center justify-center p-6 border border-destructive/50 rounded-md">
                    <p className="text-destructive">Error loading PDF. Please try again later.</p>
                  </div>
                }
              >
                {documentLoaded && (
                  <Page
                    key={`page_${pageNumber}_${scale}`}
                    pageNumber={pageNumber}
                    scale={scale}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    onLoadSuccess={onRenderSuccess}
                    onRenderError={(err) => {
                      console.error("Error rendering page:", err);
                      // Don't set error state for transport destroyed errors as they're typically
                      // transient errors during page navigation
                      if (err.message !== "Transport destroyed") {
                        setError(err);
                      }
                    }}
                    loading={<Skeleton className="h-[1100px] w-full" />}
                    error={
                      <div className="flex flex-col items-center justify-center p-6 border border-destructive/50 rounded-md">
                        <p className="text-destructive">Error rendering page. Please try a different page or document.</p>
                      </div>
                    }
                  />
                )}
              </Document>
              </PdfErrorBoundary>
            )}

            {/* Render placed fields */}
            {documentUrl && !signingMode &&
              fields
                .filter((field) => Number(field.pageNumber) === Number(pageNumber))
                .map((field) => (
                  <div
                    key={field.id}
                    className="absolute"
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      zIndex: 10,
                      width: field.width ? `${field.width}px` : "auto",
                      height: field.height ? `${field.height}px` : "auto",
                    }}
                  >
                    <DraggableField
                      type={field.type as any}
                      label={field.label}
                      id={field.id}
                      isPlaced
                      readOnly={field.readOnly}
                      onRemove={() => removeField(field.id)}
                      iconColor={field.iconColor}
                      signatureImage={field.signatureImage}
                      fullName={field.fullName}
                    />
                  </div>
                ))}

            {/* Render signable fields in signing mode */}
            {documentUrl && signingMode &&
              signableFields
                .filter((field) => Number(field.pageNumber) === Number(pageNumber))
                .map((field) => (
                  <div
                    key={field.id}
                    className="absolute"
                    style={{
                      left: `${field.x}%`,
                      top: `${field.y}%`,
                      zIndex: 10,
                      width: field.width ? `${field.width}px` : "auto",
                      height: field.height ? `${field.height}px` : "auto",
                    }}
                  >
                    <SignableField field={field} onClick={onFieldClick} />
                  </div>
                ))}

            {/* "Please Sign" arrow indicator for first incomplete field */}
            {signingMode && firstIncompleteField && showArrow && (
              <div
                className="absolute flex items-center z-20 pointer-events-none"
                style={{
                  top: `${firstIncompleteField.y}%`,
                  right: '100%',
                  marginRight: '4px',
                  animation: 'sign-arrow-nudge 1.5s ease-in-out infinite',
                }}
              >
                <div
                  className="flex items-center shadow-lg"
                  style={{
                    background: '#16a34a',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    padding: '5px 6px 5px 10px',
                    borderRadius: '4px 0 0 4px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  SIGN
                </div>
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: '14px solid transparent',
                    borderBottom: '14px solid transparent',
                    borderLeft: '10px solid #16a34a',
                  }}
                />
                <style>{`
                  @keyframes sign-arrow-nudge {
                    0%, 100% { transform: translateX(0); }
                    50% { transform: translateX(6px); }
                  }
                `}</style>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentCanvas;
